package com.sddamico.components.motion

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.w3c.dom.events.EventListener

/**
 * Easing curves. The built-in CSS keywords are too soft to read as deliberate, so every animation in the app
 * picks one of these instead.
 */
enum class Ease(x1: Double, y1: Double, x2: Double, y2: Double) {
    /** Anything entering, leaving, or answering a tap: starts fast so the response is felt immediately. */
    Out(0.23, 1.0, 0.32, 1.0),

    /** Something already on screen travelling to a new spot, like an underline or a reordered row. */
    InOut(0.77, 0.0, 0.175, 1.0),

    /** Panels that slide in from an edge. */
    Drawer(0.32, 0.72, 0.0, 1.0);

    val css = "cubic-bezier($x1, $y1, $x2, $y2)"
    val fn = AnimationTimingFunction.cubicBezier(x1, y1, x2, y2)
}

/** Durations in milliseconds. UI motion stays under 300ms so it never makes anyone wait. */
object Durations {
    const val PRESS = 120
    const val HOVER = 150
    const val EXIT = 150
    const val POPOVER = 180
    const val MODAL = 240
    const val MOVE = 280
    const val ENTER = 280
    const val DRAWER = 320
    const val STAGGER_STEP = 40
}

private const val REDUCED_MOTION = "(prefers-reduced-motion: reduce)"

val ReducedMotionQuery = CSSMediaQuery.Raw(REDUCED_MOTION)

/**
 * Tracks the OS "reduce motion" setting for motion driven from Kotlin (inline styles and the Web Animations API).
 * Stylesheet motion handles the setting with [ReducedMotionQuery] instead.
 */
@Composable
fun rememberReducedMotion(): Boolean {
    val query = remember { window.matchMedia(REDUCED_MOTION) }
    var reduced by remember { mutableStateOf(query.matches) }
    DisposableEffect(query) {
        val listener = EventListener { reduced = query.matches }
        query.addEventListener("change", listener)
        onDispose { query.removeEventListener("change", listener) }
    }
    return reduced
}
